"use client";

import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { useRouter } from "@/lib/i18n/navigation";
import { useLocale, useTranslations } from "next-intl";
import { SPECIALTIES, CITIES, getSpecialty, getCity, getLocalizedConditions } from "@/lib/data/specialties";
import { SYMPTOMS, type SymptomEntry } from "@/lib/data/symptoms";
import actsIndexJson from "@/lib/data/acts-index.json";
import { localizedField, localizedName } from "@/lib/data/i18n-helpers";
import { normalizeQuery } from "@/lib/search/normalize";
import { bounded_levenshtein } from "@/lib/search/match";
import { physicianUrlFromHit } from "@/lib/data/url-builder";
import { track } from "@/lib/observability/analytics";
import { cn } from "@/lib/utils";

// ═══════════════════ Acts index (pré-calculé au build depuis PHYSICIANS_ALL) ═══════════════════
// Ship un petit JSON (~250KB, 3275 entrées) au lieu de 98MB de données médecins
// dans le bundle client.
type ActData = { count: number; topSpecialty: string };
const ACTS_INDEX: Record<string, ActData> = actsIndexJson as Record<string, ActData>;
let _actMapCache: Map<string, ActData> | null = null;
function getActIndex(): Map<string, ActData> {
  if (_actMapCache) return _actMapCache;
  _actMapCache = new Map(Object.entries(ACTS_INDEX));
  return _actMapCache;
}

// ═══════════════════ Types ═══════════════════

type SpecialtyHit = {
  kind: "specialty";
  slug: string;
  label: string;
  icon: string;
  sub?: string;
  score: number;
};
type SymptomHit = {
  kind: "symptom";
  label: string;
  primarySlug: string;
  primaryLabel: string;
  alternates: string[];
  category: SymptomEntry["category"];
  score: number;
};
type CityHit = {
  kind: "city";
  slug: string;
  label: string;
  sub: string;
  score: number;
};
type CombinedHit = {
  kind: "combined";
  specialty: { slug: string; label: string; icon: string };
  city: { slug: string; label: string };
  score: number;
};
type QHit = SpecialtyHit | SymptomHit | CombinedHit;

// Suggestions externes (fetchées depuis /api/search/suggest).
// Pas dans QHit car elles ne sont pas matchées localement et ne participent
// pas au scoring/dedup local — elles s'affichent dans des sections dédiées
// du dropdown.
export type PhysicianSuggestion = {
  slug: string;
  name: string;
  /** Label localisé pour l'affichage (ex. "Dentiste") */
  specialty: string;
  /** Slug lowercase pour construire l'URL canonique (ex. "dentiste") */
  specialtySlug?: string;
  citySlug: string;
  cityName: string;
  photoUrl?: string;
  isCenter: boolean;
  tier: string;
  /** Score serveur (0-100+ avec boost tier). Permet le re-ordering vs hits locaux. */
  score?: number;
};
export type ClinicSuggestion = {
  slug: string;
  name: string;
  type: string;
  citySlug: string;
  cityName: string;
  photoEmoji: string;
  /** Score serveur. */
  score?: number;
};
export type MedicamentSuggestion = {
  slug: string;
  brand: string;
  /** Sous-ligne : dosage · forme · labo · prix MAD (déjà formaté serveur). */
  hint: string | null;
  score?: number;
};

const RECENT_KEY = "sahha:search:recent";
const MAX_RECENT = 5;

// ═══════════════════ Normalize + score ═══════════════════

/**
 * Normalisation locale alias de normalizeQuery() \u2014 g\u00e8re :
 * - latin (lowercase, NFD, strip diacritiques, ponctuation)
 * - arabe (tashk\u012bl, alif variants \u0623|\u0625|\u0622|\u0671\u2192\u0627, ya/maksura \u0649\u2192\u064a,
 *   ta marbouta \u0629\u2192\u0647, hamza isol\u00e9e)
 *
 * Indispensable pour que \u00ab \u0637\u0628\u064a\u0628 \u0639\u0627\u0645 \u00bb trouve la sp\u00e9cialit\u00e9 g\u00e9n\u00e9raliste
 * et \u00ab \u0627\u0644\u062f\u0627\u0631 \u0627\u0644\u0628\u064a\u0636\u0627\u0621 \u00bb la ville Casablanca dans le scoring client-side.
 */
const norm = normalizeQuery;

// Alias local : la SearchBar utilisait sa propre `levenshtein()` dupliquée
// de lib/search/match.bounded_levenshtein. Dette technique nettoyée
// 2026-05-11 — on importe désormais l'unique implémentation.
const levenshtein = bounded_levenshtein;

/**
 * Score needle vs haystack — returns 0-100.
 * Multi-word aware: chaque token du needle doit match au moins un token/prefix
 * de la haystack. Score agrégé par moyenne pondérée.
 */
function scoreMatch(needle: string, hay: string): number {
  if (!needle || !hay) return 0;
  if (hay === needle) return 100;
  if (hay.startsWith(needle)) return 94;

  const needleTokens = needle.split(" ").filter((t) => t.length >= 2);
  const hayTokens = hay.split(" ");
  if (needleTokens.length === 0) return 0;

  let total = 0, matched = 0;
  for (const nt of needleTokens) {
    let best = 0;
    for (const ht of hayTokens) {
      if (ht === nt) { best = Math.max(best, 95); continue; }
      if (ht.startsWith(nt)) { best = Math.max(best, 85); continue; }
      if (nt.length >= 4 && ht.includes(nt)) { best = Math.max(best, 60); continue; }
      // Fuzzy for tokens >= 5 chars
      if (nt.length >= 5 && ht.length >= 3) {
        const maxDist = nt.length >= 7 ? 2 : 1;
        const d = levenshtein(nt, ht, maxDist);
        if (d <= maxDist) best = Math.max(best, 60 - d * 15);
      }
    }
    if (best > 0) { matched++; total += best; }
  }
  if (matched === 0) return 0;
  const coverage = matched / needleTokens.length;
  return Math.round((total / needleTokens.length) * coverage);
}

// ═══════════════════ Intent engine ═══════════════════

/** Tente de séparer un query combiné : "dentiste rabat" → { q: "dentiste", ville: "rabat" } */
function splitCombined(q: string, locale: string): { q: string; ville: string } | null {
  const term = norm(q);
  if (!term) return null;
  const tokens = term.split(" ");
  if (tokens.length < 2) return null;

  // Scanne toutes les coupures possibles : gauche = spé/symptôme, droite = ville
  for (let cut = 1; cut < tokens.length; cut++) {
    const left = tokens.slice(0, cut).join(" ");
    const right = tokens.slice(cut).join(" ");

    // Left doit matcher une spé ou un symptôme avec score >= 80
    const leftSpe = bestSpeMatch(left, locale);
    const leftSym = bestSymMatch(left, locale);
    const leftScore = Math.max(leftSpe?.score ?? 0, leftSym?.score ?? 0);
    // Right doit matcher une ville avec score >= 80
    const rightCity = bestCityMatch(right, locale);
    if (leftScore >= 80 && rightCity && rightCity.score >= 80) {
      return { q: left, ville: right };
    }

    // Essai inverse : left=ville, right=spé
    const rightSpe = bestSpeMatch(right, locale);
    const rightSym = bestSymMatch(right, locale);
    const rightScore = Math.max(rightSpe?.score ?? 0, rightSym?.score ?? 0);
    const leftCity = bestCityMatch(left, locale);
    if (rightScore >= 80 && leftCity && leftCity.score >= 80) {
      return { q: right, ville: left };
    }
  }
  return null;
}

function bestSpeMatch(term: string, locale: string): { slug: string; label: string; icon: string; score: number } | null {
  let best: { slug: string; label: string; icon: string; score: number } | null = null;
  for (const s of SPECIALTIES) {
    const score = Math.max(
      scoreMatch(term, norm(s.nameFr)),
      scoreMatch(term, norm(s.slug)),
      scoreMatch(term, norm(s.nameEn)),
      scoreMatch(term, norm(s.nameAr ?? "")),
    );
    if (score > 0 && (!best || score > best.score)) {
      best = { slug: s.slug, label: localizedName(s, locale), icon: s.icon, score };
    }
  }
  return best;
}

function bestSymMatch(term: string, locale: string): { label: string; primarySlug: string; score: number } | null {
  let best: { label: string; primarySlug: string; score: number } | null = null;
  for (const s of SYMPTOMS) {
    const score = Math.max(
      scoreMatch(term, norm(s.labelFr)),
      scoreMatch(term, norm(s.labelAr ?? "")),
      scoreMatch(term, norm(s.labelEn ?? "")),
      ...s.aliases.map((a) => scoreMatch(term, norm(a))),
    );
    if (score > 0 && (!best || score > best.score)) {
      best = { label: localizedField(s, "label", locale), primarySlug: s.primary, score };
    }
  }
  return best;
}

function bestCityMatch(term: string, locale: string): { slug: string; label: string; score: number } | null {
  let best: { slug: string; label: string; score: number } | null = null;
  for (const c of CITIES) {
    const score = Math.max(
      scoreMatch(term, norm(c.nameFr)),
      scoreMatch(term, norm(c.nameAr ?? "")),
      scoreMatch(term, norm(c.nameEn ?? "")),
      scoreMatch(term, norm(c.slug)),
    );
    if (score > 0 && (!best || score > best.score)) {
      best = { slug: c.slug, label: localizedName(c, locale), score };
    }
  }
  return best;
}

function searchIntent(q: string, locale: string): QHit[] {
  const term = norm(q);
  if (!term) return [];

  const hits: QHit[] = [];

  // 0) COMBINED — détection "dentiste rabat"
  const combo = splitCombined(q, locale);
  if (combo) {
    const spe = bestSpeMatch(combo.q, locale);
    const sym = bestSymMatch(combo.q, locale);
    const city = bestCityMatch(combo.ville, locale);
    if (city) {
      if (spe) {
        hits.push({
          kind: "combined",
          specialty: { slug: spe.slug, label: spe.label, icon: spe.icon },
          city: { slug: city.slug, label: city.label },
          score: 98,
        });
      } else if (sym) {
        const speInfo = getSpecialty(sym.primarySlug);
        if (speInfo) {
          hits.push({
            kind: "combined",
            specialty: { slug: speInfo.slug, label: localizedName(speInfo, locale), icon: speInfo.icon },
            city: { slug: city.slug, label: city.label },
            score: 97,
          });
        }
      }
    }
  }

  // 1) SPECIALTIES — haystack inclut TOUTES locales pour matching multilingue
  // (AR users qui tapent "إنفلونزا" doivent matcher "Grippe" via la spe généraliste).
  for (const s of SPECIALTIES) {
    const haystacks = [
      s.nameFr, s.nameEn, s.nameAr ?? "", s.slug,
      ...s.commonConditions,
      ...(s.commonConditionsAr ?? []),
      ...(s.commonConditionsEn ?? []),
    ].map(norm);
    const score = Math.max(...haystacks.map((h) => scoreMatch(term, h)));
    if (score > 20) {
      hits.push({
        kind: "specialty",
        slug: s.slug,
        label: localizedName(s, locale),
        icon: s.icon,
        sub: getLocalizedConditions(s, locale).slice(0, 2).join(" · "),
        score: score + (s.featured ? 3 : 0),
      });
    }
  }

  // 2) SYMPTOMS
  for (const sym of SYMPTOMS) {
    const haystacks = [sym.labelFr, sym.labelAr ?? "", sym.labelEn ?? "", ...sym.aliases].map(norm);
    const score = Math.max(...haystacks.map((h) => scoreMatch(term, h)));
    if (score > 20) {
      const primarySpe = getSpecialty(sym.primary);
      if (!primarySpe) continue;
      hits.push({
        kind: "symptom",
        label: localizedField(sym, "label", locale),
        primarySlug: sym.primary,
        primaryLabel: localizedName(primarySpe, locale),
        alternates: sym.alternates ?? [],
        category: sym.category,
        score,
      });
    }
  }

  // 3) ACTS — actes scrapés depuis PHYSICIANS_ALL (lazy index)
  const actIndex = getActIndex();
  for (const [actLabel, actData] of actIndex.entries()) {
    if (actData.count < 2) continue; // ignore actes rares (erreurs de transcription)
    const score = scoreMatch(term, norm(actLabel));
    if (score > 30) {
      const actSpe = getSpecialty(actData.topSpecialty);
      hits.push({
        kind: "symptom",          // réutilise le rendu symptom (→ spé)
        label: actLabel,
        primarySlug: actData.topSpecialty,
        primaryLabel: actSpe ? localizedName(actSpe, locale) : actData.topSpecialty,
        alternates: [],
        category: "soin",
        score: score - 5,          // prio un peu moins haute que SYMPTOMS curaté
      });
    }
  }

  // Dédoublonner spé déjà proposée par un symptôme à score comparable
  const dedup = new Map<string, QHit>();
  for (const h of hits) {
    const key = h.kind === "specialty" ? `spe-${h.slug}` : h.kind === "symptom" ? `sym-${h.label}` : `combo-${h.specialty.slug}-${h.city.slug}`;
    const prev = dedup.get(key);
    if (!prev || h.score > prev.score) dedup.set(key, h);
  }

  return Array.from(dedup.values()).sort((a, b) => b.score - a.score).slice(0, 9);
}

function searchCities(v: string, locale: string): CityHit[] {
  const term = norm(v);
  if (!term) return [];
  return CITIES.map((c) => {
    const slots = [c.nameFr, c.nameAr, c.nameEn ?? "", c.slug, c.region].map(norm);
    const score = Math.max(...slots.map((h) => scoreMatch(term, h)));
    return { c, score };
  })
    .filter((x) => x.score > 20)
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      if (a.c.featured !== b.c.featured) return a.c.featured ? -1 : 1;
      return a.c.nameFr.localeCompare(b.c.nameFr);
    })
    .slice(0, 12)
    .map(({ c, score }) => ({
      kind: "city" as const,
      slug: c.slug,
      label: localizedName(c, locale),
      sub: c.region,
      score,
    }));
}

// ═══════════════════ Popular suggestions ═══════════════════

type PopularQ = { label: string; spe: string; icon: string; isSymptom?: boolean };
// Slugs uniquement — les labels sont résolus à l'exécution via getCity(slug)
// + localizedName() pour respecter la locale active (FR/AR/EN). Évite que les
// villes populaires affichent "Casablanca" sur la version arabe du site.
const POPULAR_CITY_SLUGS: ReadonlyArray<{ slug: string; icon: string }> = [
  { slug: "casablanca", icon: "🏙️" },
  { slug: "rabat", icon: "🏛️" },
  { slug: "marrakech", icon: "🕌" },
  { slug: "fes", icon: "🏺" },
  { slug: "tanger", icon: "⛵" },
  { slug: "agadir", icon: "🏖️" },
  { slug: "meknes", icon: "🏰" },
  { slug: "oujda", icon: "🌅" },
];

// ═══════════════════ Inline ghost completion ═══════════════════

function findGhost(term: string, pool: string[]): string {
  if (!term) return "";
  const nTerm = norm(term);
  for (const p of pool) {
    const nP = norm(p);
    if (nP.startsWith(nTerm) && nP.length > nTerm.length) {
      return p.slice(term.length);
    }
  }
  return "";
}

// ═══════════════════ Highlight ═══════════════════

function Highlight({ text, term }: { text: string; term: string }) {
  if (!term) return <>{text}</>;
  const nt = norm(text);
  const nTerm = norm(term);
  const idx = nt.indexOf(nTerm);
  if (idx < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-transparent font-semibold text-[var(--sahha-primary-700)]">
        {text.slice(idx, idx + nTerm.length)}
      </mark>
      {text.slice(idx + nTerm.length)}
    </>
  );
}

// ═══════════════════ Icons ═══════════════════

function IconSearch({ className }: { className?: string }) {
  return (<svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden><circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2"/><path d="m20 20-3.5-3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>);
}
function IconPin({ className }: { className?: string }) {
  return (<svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden><path d="M12 22s7-6.5 7-12a7 7 0 1 0-14 0c0 5.5 7 12 7 12Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/><circle cx="12" cy="10" r="2.5" stroke="currentColor" strokeWidth="2"/></svg>);
}
function IconClose({ className }: { className?: string }) {
  return (<svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden><path d="M6 6l12 12M18 6 6 18" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"/></svg>);
}
function IconArrow({ className }: { className?: string }) {
  return (<svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden><path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>);
}
function IconClock({ className }: { className?: string }) {
  return (<svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2"/><path d="M12 7v5l3 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>);
}
function IconSparkle({ className }: { className?: string }) {
  return (<svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden><path d="M12 3v4M12 17v4M3 12h4M17 12h4M6 6l2.5 2.5M15.5 15.5 18 18M6 18l2.5-2.5M15.5 8.5 18 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>);
}
function IconFire({ className }: { className?: string }) {
  return (<svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden><path d="M12 3s3 4 3 7a3 3 0 0 1-6 0c0-1 .5-2 1-3-2 2-4 5-4 8a6 6 0 1 0 12 0c0-4-3-7-6-12Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/></svg>);
}
function IconGeo({ className }: { className?: string }) {
  return (<svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden><circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="2"/><path d="M12 2v3M12 19v3M2 12h3M19 12h3" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5" strokeDasharray="2 3"/></svg>);
}

// ═══════════════════ Category badge ═══════════════════

function CategoryBadge({ category, label }: { category: SymptomEntry["category"]; label: string }) {
  const classes: Record<SymptomEntry["category"], string> = {
    symptome: "bg-amber-50 text-amber-700",
    pathologie: "bg-red-50 text-red-700",
    examen: "bg-indigo-50 text-indigo-700",
    soin: "bg-emerald-50 text-emerald-700",
    motif: "bg-sky-50 text-sky-700",
  };
  return (
    <span className={cn("rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider", classes[category])}>
      {label}
    </span>
  );
}

// ═══════════════════ Component ═══════════════════

export function SearchBar({
  defaultQ = "",
  defaultVille = "",
  variant = "hero",
}: {
  defaultQ?: string;
  defaultVille?: string;
  variant?: "hero" | "inline" | "compact";
}) {
  const router = useRouter();
  const t = useTranslations("search");
  const locale = useLocale();
  // Defensive : t.raw() peut renvoyer undefined si le namespace 'search'
  // n'est pas sérialisé pour la route courante (cf. client-namespaces-by-route).
  // Sans ce guard, .map() throw "N.map is not a function" et tout SearchBar
  // crash (incident SAHHA-16 2026-06-03 : SearchBar rendu sur /medicaments
  // et /pharmacie-de-garde qui n'avaient pas 'search' au whitelist).
  const POPULAR_Q: PopularQ[] = (() => {
    const raw = t.raw("bar.popular");
    return Array.isArray(raw) ? (raw as PopularQ[]) : [];
  })();
  const [q, setQ] = useState(defaultQ);
  const [ville, setVille] = useState(defaultVille);
  const [open, setOpen] = useState<"q" | "ville" | null>(null);
  const [highlight, setHighlight] = useState(0);
  const [recent, setRecent] = useState<{ q: string; ville: string; at: number }[]>([]);
  const [geoLoading, setGeoLoading] = useState(false);

  // ── Hits dynamiques depuis /api/search/suggest (médecins + cliniques) ──
  // Le bundle client n'embarque pas les 30k médecins. On fetch en debounce
  // 200ms à chaque saisie ≥ 2 caractères. Permet à l'utilisateur de trouver
  // « Dr. Mohamed Bennani » ou « Clinique Agdal » directement dans le dropdown
  // sans devoir submit + filtrer côté résultats.
  const [physicianHits, setPhysicianHits] = useState<PhysicianSuggestion[]>([]);
  const [clinicHits, setClinicHits] = useState<ClinicSuggestion[]>([]);
  const [medicamentHits, setMedicamentHits] = useState<MedicamentSuggestion[]>([]);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) {
      setPhysicianHits([]);
      setClinicHits([]);
      setMedicamentHits([]);
      return;
    }
    const ctrl = new AbortController();
    const timer = setTimeout(() => {
      fetch(
        `/api/search/suggest?q=${encodeURIComponent(term)}&locale=${locale}&limit=5`,
        { signal: ctrl.signal },
      )
        .then((r) => (r.ok ? r.json() : { physicians: [], clinics: [], medicaments: [] }))
        .then((data: {
          physicians: PhysicianSuggestion[];
          clinics: ClinicSuggestion[];
          medicaments?: MedicamentSuggestion[];
        }) => {
          setPhysicianHits(data.physicians ?? []);
          setClinicHits(data.clinics ?? []);
          setMedicamentHits(data.medicaments ?? []);
        })
        .catch(() => {
          // Aborted ou erreur réseau — silence (le matching local couvre déjà
          // spécialités/villes/symptômes, l'utilisateur n'est pas bloqué).
        });
    }, 200);
    return () => {
      clearTimeout(timer);
      ctrl.abort();
    };
  }, [q, locale]);

  const qRef = useRef<HTMLInputElement>(null);
  const villeRef = useRef<HTMLInputElement>(null);

  // Global Cmd/Ctrl+K to focus search
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        qRef.current?.focus();
        qRef.current?.select();
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(RECENT_KEY);
      if (raw) setRecent(JSON.parse(raw));
    } catch { /* ignore */ }
  }, []);

  const saveRecent = useCallback(
    (entry: { q: string; ville: string }) => {
      if (!entry.q && !entry.ville) return;
      try {
        const next = [
          { ...entry, at: Date.now() },
          ...recent.filter((r) => !(r.q === entry.q && r.ville === entry.ville)),
        ].slice(0, MAX_RECENT);
        setRecent(next);
        localStorage.setItem(RECENT_KEY, JSON.stringify(next));
      } catch { /* ignore */ }
    },
    [recent],
  );

  const qHits = useMemo(() => searchIntent(q, locale), [q, locale]);
  const cityHits = useMemo(() => searchCities(ville, locale), [ville, locale]);

  // Re-ordering intelligent : si l'utilisateur tape un nom propre (ex.
  // « oulaya bou »), les hits locaux fuzzy (Mal de ventre, Acné…) ont un
  // score faible (~40) mais s'affichent quand même. Les médecins/cliniques
  // matchés via l'API ont eux un score fort (≥80) et sont plus pertinents.
  //
  // Règle :
  //  - qHits[0].score ≥ 88  → match local fort sur une spécialité/symptôme
  //                            reconnu → on garde l'ordre actuel (qHits en haut)
  //  - sinon                → médecins/cliniques en haut, et on filtre les
  //                            qHits faibles (<70) qui polluent le dropdown
  const topQHitScore = qHits.length > 0 ? qHits[0].score : 0;
  const externalHasResult =
    physicianHits.length > 0 ||
    clinicHits.length > 0 ||
    medicamentHits.length > 0;
  const physiciansFirst = externalHasResult && topQHitScore < 88;
  const filteredQHits = physiciansFirst
    ? qHits.filter((h) => h.score >= 70)
    : qHits;

  // Ghost completion
  const qGhost = useMemo(() => {
    if (!q || qHits.length === 0) return "";
    const first = qHits[0];
    const label = first.kind === "combined" ? first.specialty.label
      : first.kind === "specialty" ? first.label
      : first.kind === "symptom" ? first.label : "";
    return findGhost(q, [label]);
  }, [q, qHits]);
  const villeGhost = useMemo(() => {
    if (!ville || cityHits.length === 0) return "";
    return findGhost(ville, [cityHits[0].label]);
  }, [ville, cityHits]);

  type ActiveHit = QHit | CityHit;
  const activeHits: ActiveHit[] =
    open === "q" ? qHits : open === "ville" ? cityHits : [];
  const activeTerm = open === "q" ? q : open === "ville" ? ville : "";
  const activeFilled =
    (open === "q" && q.trim().length > 0) ||
    (open === "ville" && ville.trim().length > 0);
  const showEmpty = activeFilled && activeHits.length === 0;

  useEffect(() => { setHighlight(0); }, [q, ville, open]);

  // Resync local state with prop defaults when URL/searchParams change
  // (Next.js client navigation re-passes new defaults but useState doesn't auto-update)
  useEffect(() => {
    setQ(defaultQ);
  }, [defaultQ]);
  useEffect(() => {
    setVille(defaultVille);
  }, [defaultVille]);

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      const target = e.target as Element | null;
      if (!target) return;
      // Si la target n'est plus connectée au DOM (cas où un bouton du
      // dropdown vient d'être démonté entre mousedown et click), on
      // n'évalue PAS closest() qui retournerait null à tort et fermerait
      // le dropdown inopinément.
      if (!target.isConnected) return;
      if (!target.closest("[data-search-bar]")) setOpen(null);
    }
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, []);

  function submitWith(values: { q?: string; ville?: string; pickCity?: boolean; source?: "form_submit" | "suggestion_click" | "popular" | "history" | "city_pick" }) {
    const finalQ = values.q ?? q;
    const finalVille = values.ville ?? ville;
    // GUARD QUERY VIDE : abort si query+ville vides (ou query <2 chars sans ville).
    // Sans ce guard, Enter sur form vide -> router.push('/recherche?') -> pollue
    // analytics (393 events vides historiques = 67% du bruit dashboard).
    // pickCity reste autorisé : c'est un intent UX explicite (ouvrir le picker mobile).
    const qTrim = finalQ.trim();
    const villeTrim = finalVille.trim();
    if (!values.pickCity && qTrim.length < 2 && !villeTrim) {
      qRef.current?.focus(); // UX : refocus input au lieu de naviguer
      return;
    }
    const params = new URLSearchParams();
    if (finalQ.trim()) params.set("q", finalQ.trim());
    if (finalVille.trim()) params.set("ville", finalVille.trim());
    // pickCity=1 force l'ouverture du CityPickerMobile sur /recherche
    // (utilisé par le compact searchbar pour que mobile prompte la ville).
    if (values.pickCity && !finalVille.trim()) params.set("pickCity", "1");
    saveRecent({ q: finalQ.trim(), ville: finalVille.trim() });
    setOpen(null);
    // Tracking analytics fire-and-forget : capture le submit avant le
    // router.push pour ne pas dépendre de la page de destination
    // (ex: redirect SEO `/recherche?q=Dentiste` → `/dentiste/casablanca`
    // qui court-circuite le tracking server-side de /recherche).
    if (finalQ.trim() || finalVille.trim()) {
      // GA4 client-side (sendBeacon) : event search:performed.
      // 2026-06-22 : ajout `source` pour distinguer vrais submits form vs
      // clics suggestions autocomplete vs popular vs historique (audit GA4
      // remontait gros volume sur home / sans distinction = perception
      // gonflee). Distribution typique : suggestion_click ~50%, form_submit
      // ~30%, popular ~10%, history ~5%, city_pick ~5%.
      track("search:performed", {
        q: finalQ.trim() || undefined,
        ville: finalVille.trim() || undefined,
        locale,
        source: values.source || "form_submit",
      });
      try {
        fetch("/api/track/search", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            q: finalQ.trim(),
            ville: finalVille.trim(),
            locale,
            source: `searchbar-${values.source || "form_submit"}`,
          }),
          keepalive: true,
          credentials: "same-origin",
        }).catch(() => {});
      } catch {
        // ignore : analytics ne doit jamais empêcher la navigation
      }
    }
    // Routing intelligent : si l'autocomplete a remonté des médicaments
    // (score serveur ≥55) ET aucun médecin/clinique pertinent (≤2 matches),
    // l'intent dominant est "médicament" → route directement vers /medicaments?q=...
    // (la page hub recherche médicaments) plutôt que /recherche (annuaire médecins).
    //
    // Évite le scénario où l'utilisateur tape "doliprane" et atterrit sur une
    // page annuaire vide avant de voir la section médicaments en bas.
    const looksLikeMedicament =
      medicamentHits.length > 0 &&
      physicianHits.length <= 1 &&
      clinicHits.length === 0 &&
      !finalVille.trim();
    if (looksLikeMedicament && finalQ.trim()) {
      router.push(`/medicaments?q=${encodeURIComponent(finalQ.trim())}` as never);
      return;
    }
    // SENTINEL `_t=1` — marque l'intent submit pour <SearchOutcomeBeacon/>.
    // Sans ce flag, le beacon /recherche skip et la cascadeStage n'est PAS
    // remontée en DB. Présent uniquement sur vrais submits (form / suggestion /
    // popular / history / city_pick) — absent sur pageloads SEO, refresh, back,
    // navigation interne (cornerstone), bots préchargeurs. C'est ce qui évite
    // la pollution x60 qui a motivé le revert 390d6aedd. Cf. PR2 cablage
    // cascadeStage 2026-06-22.
    params.set("_t", "1");
    router.push(`/recherche?${params.toString()}` as never);
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    submitWith({});
  }

  function pickQHit(hit: QHit) {
    if (hit.kind === "combined") {
      // URL stays canonical (FR) so /recherche page can resolve slug consistently
      const speFr = getSpecialty(hit.specialty.slug)?.nameFr ?? hit.specialty.label;
      const cityFr = getCity(hit.city.slug)?.nameFr ?? hit.city.label;
      setQ(hit.specialty.label);
      setVille(hit.city.label);
      submitWith({ q: speFr, ville: cityFr, source: "suggestion_click" });
    } else if (hit.kind === "specialty") {
      const speFr = getSpecialty(hit.slug)?.nameFr ?? hit.label;
      setQ(hit.label);
      setOpen(null);
      if (ville.trim()) submitWith({ q: speFr, source: "suggestion_click" });
      else villeRef.current?.focus();
    } else {
      const spe = getSpecialty(hit.primarySlug);
      const nextQ = spe ? localizedName(spe, locale) : hit.label;
      const nextQFr = spe ? spe.nameFr : hit.label;
      setQ(nextQ);
      setOpen(null);
      if (ville.trim()) submitWith({ q: nextQFr, source: "suggestion_click" });
      else villeRef.current?.focus();
    }
  }

  function pickCity(hit: CityHit | { slug: string; label: string }) {
    const cityFr = getCity(hit.slug)?.nameFr ?? hit.label;
    setVille(hit.label);
    setOpen(null);
    if (q.trim()) submitWith({ ville: cityFr, source: "city_pick" });
    else qRef.current?.focus();
  }

  function pickPopularQ(item: typeof POPULAR_Q[number]) {
    setQ(item.label);
    setOpen(null);
    villeRef.current?.focus();
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    const ghost = open === "q" ? qGhost : villeGhost;
    if (e.key === "Tab" && ghost) {
      e.preventDefault();
      if (open === "q") setQ(q + ghost);
      else setVille(ville + ghost);
      return;
    }
    if (!activeHits.length) {
      if (e.key === "Escape") setOpen(null);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => (h + 1) % activeHits.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => (h - 1 + activeHits.length) % activeHits.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const h = activeHits[highlight];
      if ("kind" in h && h.kind === "city") pickCity(h);
      else pickQHit(h as QHit);
    } else if (e.key === "Escape") {
      setOpen(null);
    }
  }

  function handleGeolocate() {
    if (!("geolocation" in navigator)) return;
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        // Pick nearest city from CITIES
        const { latitude, longitude } = pos.coords;
        let best: typeof CITIES[number] | null = null;
        let bestDist = Infinity;
        for (const c of CITIES) {
          const d = Math.hypot(c.lat - latitude, c.lng - longitude);
          if (d < bestDist) { bestDist = d; best = c; }
        }
        if (best) {
          setVille(localizedName(best, locale));
          setOpen(null);
          if (q.trim()) submitWith({ ville: best.nameFr });
        }
        setGeoLoading(false);
      },
      () => setGeoLoading(false),
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 },
    );
  }

  const showEmptyState =
    open !== null && !activeFilled;
  const showRecent = showEmptyState && recent.length > 0;

  if (variant === "compact") {
    // Single-input mobile pattern : Enter / submit / pick → navigue vers /recherche.
    // Si aucune ville n'est encore choisie, on ajoute pickCity=1 pour que la page
    // /recherche ouvre automatiquement le CityPickerMobile (full-screen) afin que
    // l'utilisateur sélectionne sa ville à l'étape suivante.
    function onCompactKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
      if (e.key === "Enter") {
        e.preventDefault();
        qRef.current?.blur();
        setOpen(null);
        const top = qHits[0];
        if (top) {
          if (top.kind === "specialty") {
            const speFr = getSpecialty(top.slug)?.nameFr ?? top.label;
            submitWith({ q: speFr, pickCity: true });
          } else if (top.kind === "symptom") {
            const spe = getSpecialty(top.primarySlug);
            submitWith({ q: spe ? spe.nameFr : top.label, pickCity: true });
          } else if (top.kind === "combined") {
            // Spé + ville déjà combinés → submit direct sans pickCity
            const speFr = getSpecialty(top.specialty.slug)?.nameFr ?? top.specialty.label;
            const cityFr = getCity(top.city.slug)?.nameFr ?? top.city.label;
            submitWith({ q: speFr, ville: cityFr });
          }
        } else if (q.trim()) {
          submitWith({ q, pickCity: true });
        }
      } else if (e.key === "Escape") {
        setOpen(null);
      } else if (e.key === "ArrowDown" && qHits.length > 0) {
        e.preventDefault();
        setHighlight((h) => (h + 1) % qHits.length);
      } else if (e.key === "ArrowUp" && qHits.length > 0) {
        e.preventDefault();
        setHighlight((h) => (h - 1 + qHits.length) % qHits.length);
      }
    }

    function pickCompactHit(hit: QHit) {
      qRef.current?.blur();
      setOpen(null);
      if (hit.kind === "specialty") {
        const speFr = getSpecialty(hit.slug)?.nameFr ?? hit.label;
        submitWith({ q: speFr, pickCity: true });
      } else if (hit.kind === "symptom") {
        const spe = getSpecialty(hit.primarySlug);
        submitWith({ q: spe ? spe.nameFr : hit.label, pickCity: true });
      } else if (hit.kind === "combined") {
        const speFr = getSpecialty(hit.specialty.slug)?.nameFr ?? hit.specialty.label;
        const cityFr = getCity(hit.city.slug)?.nameFr ?? hit.city.label;
        submitWith({ q: speFr, ville: cityFr });
      }
    }

    const showDropdown =
      open === "q" &&
      q.length > 0 &&
      (qHits.length > 0 || physicianHits.length > 0 || clinicHits.length > 0 || medicamentHits.length > 0);

    return (
      <form
        data-search-bar
        onSubmit={(e) => {
          e.preventDefault();
          qRef.current?.blur();
          setOpen(null);
          if (q.trim()) submitWith({ q, pickCity: true });
        }}
        role="search"
        className="relative w-full"
      >
        <div className="relative flex w-full items-center gap-2 rounded-full border border-[var(--sahha-border-default)] bg-white p-1.5 shadow-[var(--sahha-shadow-sm)]">
          <span className="pl-2 text-[var(--sahha-text-500)]">
            <IconSearch className="h-4 w-4" />
          </span>
          <input
            ref={qRef}
            type="search"
            name="search_query_sahha"
            value={q}
            onChange={(e) => { setQ(e.target.value); setOpen("q"); setHighlight(0); }}
            onFocus={() => setOpen("q")}
            onBlur={(e) => {
              const next = e.relatedTarget as Element | null;
              if (next?.closest('[data-search-bar]')) return;
              setOpen(null);
            }}
            onKeyDown={onCompactKeyDown}
            placeholder={t("bar.compactPlaceholder")}
            className="sb-input flex-1 bg-transparent py-1.5 text-[15px] font-medium text-[var(--sahha-text-900)] placeholder:font-normal placeholder:text-[var(--sahha-text-400)]"
            autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false}
            data-form-type="other" data-lpignore="true" data-1p-ignore="true" data-bwignore="true"
            enterKeyHint="search"
          />
          {q.length > 0 && (
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => { setQ(""); qRef.current?.focus(); }}
              aria-label={t("bar.clearLabel")}
              className="flex h-7 w-7 items-center justify-center rounded-full text-[var(--sahha-text-400)] hover:bg-[var(--sahha-surface-1)] hover:text-[var(--sahha-text-700)]"
            >
              <IconClose className="h-4 w-4" />
            </button>
          )}
          <button type="submit" className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[var(--sahha-primary-600)] text-white hover:bg-[var(--sahha-primary-700)]" aria-label={t("bar.submitLabel")}>
            <IconSearch className="h-4 w-4" />
          </button>
        </div>

        {showDropdown && (
          <div
            role="listbox"
            className="absolute start-0 end-0 top-[calc(100%+8px)] z-50 max-h-[60vh] overflow-y-auto rounded-2xl border border-[var(--sahha-border-default)] bg-white shadow-[0_20px_40px_-10px_rgba(15,30,58,0.25)]"
            onMouseDown={(e) => e.preventDefault()}
          >
            {physiciansFirst && (
              <PhysicianClinicSections
                physicianHits={physicianHits}
                clinicHits={clinicHits}
                tPhysiciansSection={t("bar.physiciansSection", { count: physicianHits.length })}
                tClinicsSection={t("bar.clinicsSection", { count: clinicHits.length })}
                onPickPhysician={(h) => { qRef.current?.blur(); setOpen(null); router.push(physicianUrlFromHit(h) as never); }}
                onPickClinic={(slug) => { qRef.current?.blur(); setOpen(null); router.push(`/cliniques/${slug}` as never); }}
                medicamentHits={medicamentHits}
                tMedicamentsSection={t("bar.medicamentsSection", { count: medicamentHits.length })}
                tMedicamentsSeeAll={t("bar.medicamentsSeeAll", { q: q.trim() })}
                onPickMedicament={(slug) => { qRef.current?.blur(); setOpen(null); router.push(`/medicaments/${slug}` as never); }}
                onSeeAllMedicaments={() => { qRef.current?.blur(); setOpen(null); router.push(`/medicaments?q=${encodeURIComponent(q.trim())}` as never); }}
              />
            )}
            {filteredQHits.slice(0, 12).map((h, i) => (
              h.kind === "combined" ? (
                <CombinedRow key={`co-${h.specialty.slug}-${h.city.slug}`} hit={h} active={i === highlight} onMouseEnter={() => setHighlight(i)} onClick={() => pickCompactHit(h)} />
              ) : h.kind === "specialty" ? (
                <SpecialtyRow key={`sp-${h.slug}`} hit={h} term={q} active={i === highlight} onMouseEnter={() => setHighlight(i)} onClick={() => pickCompactHit(h)} />
              ) : (
                <SymptomRow key={`sy-${h.label}`} hit={h} term={q} active={i === highlight} onMouseEnter={() => setHighlight(i)} onClick={() => pickCompactHit(h)} />
              )
            ))}
            {!physiciansFirst && (
              <PhysicianClinicSections
                physicianHits={physicianHits}
                clinicHits={clinicHits}
                tPhysiciansSection={t("bar.physiciansSection", { count: physicianHits.length })}
                tClinicsSection={t("bar.clinicsSection", { count: clinicHits.length })}
                onPickPhysician={(h) => { qRef.current?.blur(); setOpen(null); router.push(physicianUrlFromHit(h) as never); }}
                onPickClinic={(slug) => { qRef.current?.blur(); setOpen(null); router.push(`/cliniques/${slug}` as never); }}
                medicamentHits={medicamentHits}
                tMedicamentsSection={t("bar.medicamentsSection", { count: medicamentHits.length })}
                tMedicamentsSeeAll={t("bar.medicamentsSeeAll", { q: q.trim() })}
                onPickMedicament={(slug) => { qRef.current?.blur(); setOpen(null); router.push(`/medicaments/${slug}` as never); }}
                onSeeAllMedicaments={() => { qRef.current?.blur(); setOpen(null); router.push(`/medicaments?q=${encodeURIComponent(q.trim())}` as never); }}
              />
            )}
            <button
              type="button"
              onClick={() => {
                qRef.current?.blur();
                setOpen(null);
                if (q.trim()) submitWith({ q, pickCity: true });
              }}
              className="flex w-full items-center gap-3 border-t border-[var(--sahha-border-subtle)] px-4 py-3 text-start hover:bg-[var(--sahha-surface-1)]"
            >
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--sahha-surface-2)] text-[var(--sahha-text-600)]">
                <IconSearch className="h-4 w-4" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-[14px] font-semibold text-[var(--sahha-text-900)]">
                  {t("bar.freeSearchTitle", { q })}
                </span>
                <span className="text-[12px] text-[var(--sahha-text-500)]">{t("bar.freeSearchSub")}</span>
              </span>
              <IconArrow className="h-4 w-4 text-[var(--sahha-text-400)]" />
            </button>
          </div>
        )}
      </form>
    );
  }

  if (variant === "inline") {
    // Pill simple maiia-style — single-row : q + divider + ville + bouton "Rechercher".
    // Pas de label uppercase ni d'icône colorée pill : juste icône grise + input.
    const showQDropdown =
      open === "q" &&
      (qHits.length > 0 ||
        physicianHits.length > 0 ||
        clinicHits.length > 0 ||
        medicamentHits.length > 0 ||
        (q.length === 0 && (recent.length > 0 || POPULAR_Q.length > 0)));
    const showVilleDropdown = open === "ville" && (cityHits.length > 0 || ville.length === 0);
    return (
      <form
        data-search-bar
        onSubmit={onSubmit}
        role="search"
        className="relative w-full"
      >
        <div className="relative flex h-12 w-full items-center rounded-full border border-[var(--sahha-border-default)] bg-white p-1 pr-1 shadow-[0_2px_10px_-4px_rgba(15,30,58,0.12)]">
          {/* Q field — flex grow */}
          <div className="relative flex h-full min-w-0 flex-1 items-center gap-2 pl-4 pr-2">
            <IconSearch className="h-4 w-4 shrink-0 text-[var(--sahha-text-500)]" />
            <input
              ref={qRef}
              id="search-q-input"
              type="search"
              name="search_query_sahha"
              value={q}
              onChange={(e) => { setQ(e.target.value); setOpen("q"); }}
              onFocus={() => setOpen("q")}
              onBlur={(e) => {
              // Race condition fix : si le nouveau focus reste dans la search
              // bar (ex: l'utilisateur passe de Q à VILLE, ou clique un button
              // du dropdown), on NE ferme PAS. Sinon le setOpen('ville') que
              // l'autre input vient de poser serait écrasé par ce setOpen(null).
              const next = e.relatedTarget as Element | null;
              if (next?.closest('[data-search-bar]')) return;
              setOpen(null);
            }}
              onKeyDown={onKeyDown}
              placeholder={t("bar.qPlaceholder")}
              aria-label={t("bar.qLabelAria")}
              aria-autocomplete="list"
              aria-expanded={open === "q"}
              className="sb-input min-w-0 flex-1 bg-transparent py-1.5 text-[14px] font-medium text-[var(--sahha-text-900)] placeholder:font-normal placeholder:text-[var(--sahha-text-400)] focus:outline-none"
              autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false}
              data-form-type="other" data-lpignore="true" data-1p-ignore="true" data-bwignore="true"
            />
            {q && (
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => { setQ(""); qRef.current?.focus(); }}
                aria-label={t("bar.clearLabel")}
                className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[var(--sahha-text-400)] hover:bg-[var(--sahha-surface-1)]"
              >
                <IconClose className="h-3.5 w-3.5" />
              </button>
            )}

            {showQDropdown && (
              <Dropdown show={true} align="left">
                {filteredQHits.length > 0 || physicianHits.length > 0 || clinicHits.length > 0 || medicamentHits.length > 0 ? (
                  <>
                    {physiciansFirst && (
                      <PhysicianClinicSections
                        physicianHits={physicianHits}
                        clinicHits={clinicHits}
                        tPhysiciansSection={t("bar.physiciansSection", { count: physicianHits.length })}
                        tClinicsSection={t("bar.clinicsSection", { count: clinicHits.length })}
                        onPickPhysician={(h) => { setOpen(null); router.push(physicianUrlFromHit(h) as never); }}
                        onPickClinic={(slug) => { setOpen(null); router.push(`/cliniques/${slug}` as never); }}
                        medicamentHits={medicamentHits}
                        tMedicamentsSection={t("bar.medicamentsSection", { count: medicamentHits.length })}
                        tMedicamentsSeeAll={t("bar.medicamentsSeeAll", { q: q.trim() })}
                        onPickMedicament={(slug) => { setOpen(null); router.push(`/medicaments/${slug}` as never); }}
                        onSeeAllMedicaments={() => { setOpen(null); router.push(`/medicaments?q=${encodeURIComponent(q.trim())}` as never); }}
                      />
                    )}
                    {filteredQHits.length > 0 && (
                      <>
                        <SectionLabel><span>{t("bar.suggestionsSection", { count: filteredQHits.length })}</span></SectionLabel>
                        {filteredQHits.map((h, i) =>
                          h.kind === "combined" ? (
                            <CombinedRow key={`combo-${h.specialty.slug}-${h.city.slug}`} hit={h} active={i === highlight} onMouseEnter={() => setHighlight(i)} onClick={() => pickQHit(h)} />
                          ) : h.kind === "specialty" ? (
                            <SpecialtyRow key={`spe-${h.slug}`} hit={h} term={activeTerm} active={i === highlight} onMouseEnter={() => setHighlight(i)} onClick={() => pickQHit(h)} />
                          ) : (
                            <SymptomRow key={`sym-${h.label}`} hit={h} term={activeTerm} active={i === highlight} onMouseEnter={() => setHighlight(i)} onClick={() => pickQHit(h)} />
                          ),
                        )}
                      </>
                    )}
                    {!physiciansFirst && (
                      <PhysicianClinicSections
                        physicianHits={physicianHits}
                        clinicHits={clinicHits}
                        tPhysiciansSection={t("bar.physiciansSection", { count: physicianHits.length })}
                        tClinicsSection={t("bar.clinicsSection", { count: clinicHits.length })}
                        onPickPhysician={(h) => { setOpen(null); router.push(physicianUrlFromHit(h) as never); }}
                        onPickClinic={(slug) => { setOpen(null); router.push(`/cliniques/${slug}` as never); }}
                        medicamentHits={medicamentHits}
                        tMedicamentsSection={t("bar.medicamentsSection", { count: medicamentHits.length })}
                        tMedicamentsSeeAll={t("bar.medicamentsSeeAll", { q: q.trim() })}
                        onPickMedicament={(slug) => { setOpen(null); router.push(`/medicaments/${slug}` as never); }}
                        onSeeAllMedicaments={() => { setOpen(null); router.push(`/medicaments?q=${encodeURIComponent(q.trim())}` as never); }}
                      />
                    )}
                  </>
                ) : (
                  <>
                    {recent.length > 0 && (
                      <>
                        <SectionLabel><span>{t("bar.recentSection")}</span></SectionLabel>
                        {recent.slice(0, 3).map((r, i) => (
                          <button
                            key={`r-${i}`}
                            type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => { setQ(r.q); setVille(r.ville); submitWith({ q: r.q, ville: r.ville }); }}
                            className="flex w-full items-center gap-3 px-3.5 py-2.5 text-start hover:bg-[var(--sahha-surface-1)]"
                          >
                            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[var(--sahha-surface-1)] text-[var(--sahha-text-600)]"><IconClock className="h-4 w-4" /></span>
                            <span className="flex-1 truncate text-sm text-[var(--sahha-text-900)]">{[r.q, r.ville].filter(Boolean).join(" · ")}</span>
                          </button>
                        ))}
                      </>
                    )}
                    <SectionLabel>
                      <span className="inline-flex items-center gap-1.5">
                        <IconFire className="h-3 w-3 text-[var(--sahha-accent-500)]" /> {t("bar.popularSection")}
                      </span>
                    </SectionLabel>
                    <div className="grid grid-cols-2 gap-1 p-1.5">
                      {POPULAR_Q.map((p) => (
                        <button
                          key={p.label}
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => pickPopularQ(p)}
                          className="flex items-center gap-2 rounded-lg border border-[var(--sahha-border-subtle)] bg-white px-2.5 py-2 text-start text-xs font-medium text-[var(--sahha-text-800)] hover:border-[var(--sahha-primary-300)] hover:bg-[var(--sahha-primary-50)]"
                        >
                          <span aria-hidden className="text-base">{p.icon}</span>
                          <span className="truncate">{p.label}</span>
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </Dropdown>
            )}
          </div>

          {/* Divider vertical */}
          <span aria-hidden className="h-6 w-px shrink-0 bg-[var(--sahha-border-default)]" />

          {/* Ville field */}
          <div className="relative flex h-full min-w-0 w-[40%] max-w-[280px] items-center gap-2 pl-4 pr-2">
            <IconPin className="h-4 w-4 shrink-0 text-[var(--sahha-text-500)]" />
            <input
              ref={villeRef}
              id="search-ville-input"
              type="search"
              name="search_location_sahha"
              value={ville}
              onChange={(e) => { setVille(e.target.value); setOpen("ville"); }}
              onFocus={() => setOpen("ville")}
              onBlur={(e) => {
              // Race condition fix : si le nouveau focus reste dans la search
              // bar (ex: l'utilisateur passe de Q à VILLE, ou clique un button
              // du dropdown), on NE ferme PAS. Sinon le setOpen('ville') que
              // l'autre input vient de poser serait écrasé par ce setOpen(null).
              const next = e.relatedTarget as Element | null;
              if (next?.closest('[data-search-bar]')) return;
              setOpen(null);
            }}
              onKeyDown={onKeyDown}
              placeholder={t("bar.villePlaceholder")}
              aria-label={t("bar.villeAria")}
              aria-autocomplete="list"
              aria-expanded={open === "ville"}
              className="sb-input min-w-0 flex-1 bg-transparent py-1.5 text-[14px] font-medium text-[var(--sahha-text-900)] placeholder:font-normal placeholder:text-[var(--sahha-text-400)] focus:outline-none"
              autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false}
              data-form-type="other" data-lpignore="true" data-1p-ignore="true" data-bwignore="true"
            />
            {ville && (
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => { setVille(""); villeRef.current?.focus(); }}
                aria-label={t("bar.clearLabel")}
                className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[var(--sahha-text-400)] hover:bg-[var(--sahha-surface-1)]"
              >
                <IconClose className="h-3.5 w-3.5" />
              </button>
            )}

            {showVilleDropdown && (
              <Dropdown show={true} align="right">
                {cityHits.length > 0 ? (
                  <>
                    <SectionLabel><span>{t("bar.citiesSection", { count: cityHits.length })}</span></SectionLabel>
                    {cityHits.map((c, i) => (
                      <CityRow key={c.slug} hit={c} term={activeTerm} active={i === highlight} onMouseEnter={() => setHighlight(i)} onClick={() => pickCity(c)} />
                    ))}
                  </>
                ) : (
                  <>
                    <div className="p-1.5">
                      <button
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={handleGeolocate}
                        disabled={geoLoading}
                        className="flex w-full items-center gap-3 rounded-xl border border-[var(--sahha-primary-200)] bg-[var(--sahha-primary-50)] px-3.5 py-2.5 text-start text-sm font-medium text-[var(--sahha-primary-800)] hover:bg-[var(--sahha-primary-100)] disabled:opacity-60"
                      >
                        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-white text-[var(--sahha-primary-700)]">
                          <IconGeo className={cn("h-4 w-4", geoLoading && "animate-spin")} />
                        </span>
                        <span className="flex-1">
                          {geoLoading ? t("bar.geoLoading") : t("bar.geoLabel")}
                        </span>
                      </button>
                    </div>
                    <SectionLabel><span>{t("bar.majorCitiesSection")}</span></SectionLabel>
                    <div className="grid grid-cols-2 gap-1 p-1.5">
                      {POPULAR_CITY_SLUGS.map(({ slug, icon }) => {
                        const city = getCity(slug);
                        const label = city ? localizedName(city, locale) : slug;
                        return (
                          <button
                            key={slug}
                            type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => pickCity({ slug, label })}
                            className="flex items-center gap-2 rounded-lg border border-[var(--sahha-border-subtle)] bg-white px-2.5 py-2 text-start text-xs font-medium text-[var(--sahha-text-800)] hover:border-[var(--sahha-accent-300)] hover:bg-[var(--sahha-accent-50)]"
                          >
                            <span aria-hidden className="text-base">{icon}</span>
                            <span className="truncate">{label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}
              </Dropdown>
            )}
          </div>

          {/* Submit pill rounded-full filled */}
          <button
            type="submit"
            className="ms-1 inline-flex h-10 shrink-0 items-center justify-center rounded-full bg-[var(--sahha-primary-600)] px-5 text-[13.5px] font-semibold text-white transition-all hover:bg-[var(--sahha-primary-700)] active:scale-[0.97]"
            aria-label={t("bar.submitLabel")}
          >
            {t("bar.submitWord")}
          </button>
        </div>
      </form>
    );
  }

  return (
    <form
      data-search-bar
      onSubmit={onSubmit}
      role="search"
      className="relative"
    >
      <div className="relative overflow-visible rounded-[18px] border border-[var(--sahha-border-default)] bg-white p-1.5 shadow-[0_8px_30px_-10px_rgba(15,30,58,0.15)]">
        <div className="flex flex-col gap-1.5 sm:flex-row sm:items-stretch sm:gap-1">
          {/* Q field */}
          <Field accent="primary" label={t("bar.qLabel")} icon={<IconSearch className="h-4 w-4" />} inputId="search-q-input">
            <GhostInput ghost={qGhost}>
              <input
                ref={qRef}
                id="search-q-input"
                type="search"
                name="search_query_sahha"
                value={q}
                onChange={(e) => { setQ(e.target.value); setOpen("q"); }}
                onFocus={() => setOpen("q")}
                onKeyDown={onKeyDown}
                placeholder={t("bar.qPlaceholder")}
                className="sb-input relative z-10 w-full bg-transparent py-1.5 pr-8 text-[14px] font-medium text-[var(--sahha-text-900)] placeholder:font-normal placeholder:text-[var(--sahha-text-400)] sm:text-[15px]"
                aria-label={t("bar.qLabelAria")}
                aria-autocomplete="list"
                aria-expanded={open === "q"}
                autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false}
                data-form-type="other" data-lpignore="true" data-1p-ignore="true" data-bwignore="true"
              />
            </GhostInput>
            {q && <ClearBtn onClick={() => { setQ(""); qRef.current?.focus(); }} />}

            {open === "q" && (
              <Dropdown
                show={
                  qHits.length > 0 ||
                  physicianHits.length > 0 ||
                  clinicHits.length > 0 ||
                  medicamentHits.length > 0 ||
                  showEmpty ||
                  showEmptyState
                }
                align="left"
              >
                {filteredQHits.length > 0 || physicianHits.length > 0 || clinicHits.length > 0 || medicamentHits.length > 0 ? (
                  <>
                    {physiciansFirst && (
                      <PhysicianClinicSections
                        physicianHits={physicianHits}
                        clinicHits={clinicHits}
                        tPhysiciansSection={t("bar.physiciansSection", { count: physicianHits.length })}
                        tClinicsSection={t("bar.clinicsSection", { count: clinicHits.length })}
                        onPickPhysician={(h) => { setOpen(null); router.push(physicianUrlFromHit(h) as never); }}
                        onPickClinic={(slug) => { setOpen(null); router.push(`/cliniques/${slug}` as never); }}
                        medicamentHits={medicamentHits}
                        tMedicamentsSection={t("bar.medicamentsSection", { count: medicamentHits.length })}
                        tMedicamentsSeeAll={t("bar.medicamentsSeeAll", { q: q.trim() })}
                        onPickMedicament={(slug) => { setOpen(null); router.push(`/medicaments/${slug}` as never); }}
                        onSeeAllMedicaments={() => { setOpen(null); router.push(`/medicaments?q=${encodeURIComponent(q.trim())}` as never); }}
                      />
                    )}
                    {filteredQHits.length > 0 && (
                      <>
                        <SectionLabel><span>{t("bar.suggestionsSection", { count: filteredQHits.length })}</span></SectionLabel>
                        {filteredQHits.map((h, i) =>
                          h.kind === "combined" ? (
                            <CombinedRow key={`combo-${h.specialty.slug}-${h.city.slug}`} hit={h} active={i === highlight} onMouseEnter={() => setHighlight(i)} onClick={() => pickQHit(h)} />
                          ) : h.kind === "specialty" ? (
                            <SpecialtyRow key={`spe-${h.slug}`} hit={h} term={activeTerm} active={i === highlight} onMouseEnter={() => setHighlight(i)} onClick={() => pickQHit(h)} />
                          ) : (
                            <SymptomRow key={`sym-${h.label}`} hit={h} term={activeTerm} active={i === highlight} onMouseEnter={() => setHighlight(i)} onClick={() => pickQHit(h)} />
                          ),
                        )}
                      </>
                    )}
                    {!physiciansFirst && (
                      <PhysicianClinicSections
                        physicianHits={physicianHits}
                        clinicHits={clinicHits}
                        tPhysiciansSection={t("bar.physiciansSection", { count: physicianHits.length })}
                        tClinicsSection={t("bar.clinicsSection", { count: clinicHits.length })}
                        onPickPhysician={(h) => { setOpen(null); router.push(physicianUrlFromHit(h) as never); }}
                        onPickClinic={(slug) => { setOpen(null); router.push(`/cliniques/${slug}` as never); }}
                        medicamentHits={medicamentHits}
                        tMedicamentsSection={t("bar.medicamentsSection", { count: medicamentHits.length })}
                        tMedicamentsSeeAll={t("bar.medicamentsSeeAll", { q: q.trim() })}
                        onPickMedicament={(slug) => { setOpen(null); router.push(`/medicaments/${slug}` as never); }}
                        onSeeAllMedicaments={() => { setOpen(null); router.push(`/medicaments?q=${encodeURIComponent(q.trim())}` as never); }}
                      />
                    )}
                    <Footer hits={filteredQHits.length + physicianHits.length + clinicHits.length} />
                  </>
                ) : showEmptyState ? (
                  <>
                    {showRecent && (
                      <>
                        <SectionLabel>
                          <span>{t("bar.recentSection")}</span>
                          <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => { setRecent([]); try { localStorage.removeItem(RECENT_KEY); } catch { /* ignore */ } }} className="font-medium text-[var(--sahha-primary-700)] hover:underline">
                            {t("bar.clearRecent")}
                          </button>
                        </SectionLabel>
                        {recent.slice(0, 3).map((r, i) => (
                          <button
                            key={`r-${i}`}
                            type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => { setQ(r.q); setVille(r.ville); submitWith({ q: r.q, ville: r.ville }); }}
                            className="flex w-full items-center gap-3 px-3.5 py-2.5 text-start hover:bg-[var(--sahha-surface-1)]"
                          >
                            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[var(--sahha-surface-1)] text-[var(--sahha-text-600)]"><IconClock className="h-4 w-4" /></span>
                            <span className="flex-1 truncate text-sm text-[var(--sahha-text-900)]">{[r.q, r.ville].filter(Boolean).join(" · ")}</span>
                          </button>
                        ))}
                      </>
                    )}
                    <SectionLabel>
                      <span className="inline-flex items-center gap-1.5">
                        <IconFire className="h-3 w-3 text-[var(--sahha-accent-500)]" /> {t("bar.popularSection")}
                      </span>
                    </SectionLabel>
                    <div className="grid grid-cols-2 gap-1 p-1.5">
                      {POPULAR_Q.map((p) => (
                        <button
                          key={p.label}
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => pickPopularQ(p)}
                          className="flex items-center gap-2 rounded-lg border border-[var(--sahha-border-subtle)] bg-white px-2.5 py-2 text-start text-xs font-medium text-[var(--sahha-text-800)] hover:border-[var(--sahha-primary-300)] hover:bg-[var(--sahha-primary-50)]"
                        >
                          <span aria-hidden className="text-base">{p.icon}</span>
                          <span className="truncate">{p.label}</span>
                        </button>
                      ))}
                    </div>
                  </>
                ) : (
                  <EmptyState term={q} t={t} />
                )}
              </Dropdown>
            )}
          </Field>

          {/* Divider */}
          <div className="hidden w-px self-stretch bg-[var(--sahha-border-default)] sm:mx-0.5 sm:block" />
          <div className="mx-2 h-px bg-[var(--sahha-border-default)] sm:hidden" />

          {/* Ville field */}
          <Field accent="accent" label={t("bar.villeLabel")} icon={<IconPin className="h-4 w-4" />} inputId="search-ville-input">
            <GhostInput ghost={villeGhost}>
              <input
                ref={villeRef}
                id="search-ville-input"
                type="search"
                name="search_location_sahha"
                value={ville}
                onChange={(e) => { setVille(e.target.value); setOpen("ville"); }}
                onFocus={() => setOpen("ville")}
                onKeyDown={onKeyDown}
                placeholder={t("bar.villePlaceholder")}
                className="sb-input relative z-10 w-full bg-transparent py-1.5 pr-8 text-[14px] font-medium text-[var(--sahha-text-900)] placeholder:font-normal placeholder:text-[var(--sahha-text-400)] sm:text-[15px]"
                aria-label={t("bar.villeAria")}
                aria-autocomplete="list"
                aria-expanded={open === "ville"}
                autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false}
                data-form-type="other" data-lpignore="true" data-1p-ignore="true" data-bwignore="true"
              />
            </GhostInput>
            {ville && <ClearBtn onClick={() => { setVille(""); villeRef.current?.focus(); }} />}

            {open === "ville" && (
              <Dropdown show={cityHits.length > 0 || showEmpty || showEmptyState} align="right">
                {cityHits.length > 0 ? (
                  <>
                    <SectionLabel><span>{t("bar.citiesSection", { count: cityHits.length })}</span></SectionLabel>
                    {cityHits.map((c, i) => (
                      <CityRow key={c.slug} hit={c} term={activeTerm} active={i === highlight} onMouseEnter={() => setHighlight(i)} onClick={() => pickCity(c)} />
                    ))}
                    <Footer hits={cityHits.length} />
                  </>
                ) : showEmptyState ? (
                  <>
                    <div className="p-1.5">
                      <button
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={handleGeolocate}
                        disabled={geoLoading}
                        className="flex w-full items-center gap-3 rounded-xl border border-[var(--sahha-primary-200)] bg-[var(--sahha-primary-50)] px-3.5 py-2.5 text-start text-sm font-medium text-[var(--sahha-primary-800)] hover:bg-[var(--sahha-primary-100)] disabled:opacity-60"
                      >
                        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-white text-[var(--sahha-primary-700)]">
                          <IconGeo className={cn("h-4 w-4", geoLoading && "animate-spin")} />
                        </span>
                        <span className="flex-1">
                          {geoLoading ? t("bar.geoLoading") : t("bar.geoLabel")}
                          <span className="block text-[11px] font-normal text-[var(--sahha-primary-600)]">{t("bar.geoSub")}</span>
                        </span>
                      </button>
                    </div>
                    <SectionLabel>
                      <span className="inline-flex items-center gap-1.5">
                        <IconFire className="h-3 w-3 text-[var(--sahha-accent-500)]" /> {t("bar.majorCitiesSection")}
                      </span>
                    </SectionLabel>
                    <div className="grid grid-cols-2 gap-1 p-1.5">
                      {POPULAR_CITY_SLUGS.map(({ slug, icon }) => {
                        const city = getCity(slug);
                        const label = city ? localizedName(city, locale) : slug;
                        return (
                          <button
                            key={slug}
                            type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => pickCity({ slug, label })}
                            className="flex items-center gap-2 rounded-lg border border-[var(--sahha-border-subtle)] bg-white px-2.5 py-2 text-start text-xs font-medium text-[var(--sahha-text-800)] hover:border-[var(--sahha-accent-300)] hover:bg-[var(--sahha-accent-50)]"
                          >
                            <span aria-hidden className="text-base">{icon}</span>
                            <span className="truncate">{label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </>
                ) : (
                  <EmptyState term={ville} isCity t={t} />
                )}
              </Dropdown>
            )}
          </Field>

          {/* Submit — pill CTA inset dans la carte (pas de problème d'alignement de coins) */}
          <button
            type="submit"
            className="group/btn relative flex shrink-0 items-center justify-center gap-2 overflow-hidden rounded-xl bg-[var(--sahha-primary-600)] px-5 py-2.5 text-sm font-semibold text-white shadow-[0_4px_12px_-2px_rgba(12,122,94,0.35)] transition-all hover:bg-[var(--sahha-primary-700)] hover:shadow-[0_6px_16px_-2px_rgba(12,122,94,0.45)] sm:px-6"
            aria-label={t("bar.submitLabel")}
          >
            <IconSearch className="h-4 w-4 sm:hidden" />
            <span>{t("bar.submitWord")}</span>
            <IconArrow className="hidden h-4 w-4 transition-transform group-hover/btn:translate-x-0.5 sm:block" />
          </button>
        </div>
      </div>

    </form>
  );
}

// ═══════════════════ Sous-composants ═══════════════════

function GhostInput({ children }: { ghost: string; children: React.ReactNode }) {
  // Ghost text rendering disabled (was overlapping with typed value).
  // Autocomplete suggestions are shown in the dropdown instead.
  return <div className="relative">{children}</div>;
}



function Field({
  accent,
  label,
  icon,
  inputId,
  children,
}: {
  accent: "primary" | "accent";
  label: string;
  icon: React.ReactNode;
  inputId: string;
  children: React.ReactNode;
}) {
  const pillClass = accent === "primary"
    ? "bg-[var(--sahha-primary-100)] text-[var(--sahha-primary-700)]"
    : "bg-[var(--sahha-accent-100)] text-[var(--sahha-accent-700)]";
  const labelClass = accent === "primary"
    ? "text-[var(--sahha-primary-700)]"
    : "text-[var(--sahha-accent-700)]";
  return (
    <div className="sb-field group relative flex-1">
      <div className="flex items-center gap-2.5 px-3 pt-2 sm:gap-3 sm:px-3.5">
        <span className={cn("flex h-7 w-7 shrink-0 items-center justify-center rounded-lg", pillClass)}>{icon}</span>
        <label htmlFor={inputId} className={cn("cursor-text text-[10px] font-semibold uppercase tracking-wider sm:text-[11px]", labelClass)}>{label}</label>
      </div>
      <div className="relative pl-[48px] pr-3 pb-2 sm:pl-[54px] sm:pr-3.5">{children}</div>
    </div>
  );
}

function ClearBtn({ onClick }: { onClick: () => void }) {
  const t = useTranslations("search");
  return (
    <button
      type="button"
      aria-label={t("bar.clearLabel")}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className="absolute end-3 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full bg-[var(--sahha-surface-1)] text-[var(--sahha-text-600)] transition-colors hover:bg-[var(--sahha-surface-2)]"
    >
      <IconClose className="h-3.5 w-3.5" />
    </button>
  );
}

function Dropdown({ show, align, children }: { show: boolean; align: "left" | "right"; children: React.ReactNode }) {
  if (!show) return null;
  return (
    <div
      role="listbox"
      // onMouseDown preventDefault sur le wrapper entier — empêche le blur
      // de l'input parent quand l'utilisateur tape sur n'importe quelle zone
      // du dropdown (pas seulement les boutons). Évite que le dropdown se
      // referme au tap sur mobile (Safari iOS) avant que le click sur la
      // ville ne se déclenche.
      onMouseDown={(e) => e.preventDefault()}
      className={cn(
        "absolute top-full z-[100] mt-2 w-full max-w-[calc(100vw-2rem)] overflow-hidden rounded-2xl border border-[var(--sahha-border-default)] bg-white shadow-[0_20px_60px_-10px_rgba(15,30,58,0.25)] sm:w-[440px]",
        "animate-in fade-in slide-in-from-top-2 duration-150",
        align === "left" ? "start-2" : "end-2 sm:left-auto",
      )}
    >
      <div className="max-h-[460px] overflow-auto py-1.5">{children}</div>
    </div>
  );
}

function CombinedRow({ hit, active, onMouseEnter, onClick }: { hit: CombinedHit; active: boolean; onMouseEnter: () => void; onClick: () => void; }) {
  const tBar = useTranslations("search");
  return (
    <button
      type="button" role="option" aria-selected={active}
      onMouseDown={(e) => e.preventDefault()}
      onMouseEnter={onMouseEnter}
      onClick={onClick}
      className={cn(
        "flex w-full items-center gap-3 px-3.5 py-3 text-start",
        active ? "bg-gradient-to-r from-[var(--sahha-primary-50)] to-[var(--sahha-accent-50)]" : "hover:bg-[var(--sahha-surface-1)]",
      )}
    >
      <span aria-hidden className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[var(--sahha-primary-100)] to-[var(--sahha-accent-100)] text-xl">
        {hit.specialty.icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2">
          <span className="truncate text-sm font-semibold text-[var(--sahha-text-900)]">
            {hit.specialty.label} {tBar("bar.comboTo")} {hit.city.label}
          </span>
          <span className="shrink-0 rounded-full bg-gradient-to-r from-[var(--sahha-primary-600)] to-[var(--sahha-accent-600)] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">
            {tBar("bar.comboLabel")}
          </span>
        </span>
        <span className="mt-0.5 block truncate text-xs text-[var(--sahha-text-500)]">
          {tBar("bar.comboSub")}
        </span>
      </span>
      {active && <KbdHint />}
    </button>
  );
}

function SpecialtyRow({ hit, term, active, onMouseEnter, onClick }: { hit: SpecialtyHit; term: string; active: boolean; onMouseEnter: () => void; onClick: () => void; }) {
  const t = useTranslations("search");
  return (
    <button
      type="button" role="option" aria-selected={active}
      onMouseDown={(e) => e.preventDefault()}
      onMouseEnter={onMouseEnter}
      onClick={onClick}
      className={cn("flex w-full items-center gap-3 px-3.5 py-2.5 text-start", active ? "bg-[var(--sahha-primary-50)]" : "hover:bg-[var(--sahha-surface-1)]")}
    >
      <span aria-hidden className="shrink-0 text-xl">{hit.icon}</span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2">
          <span className="truncate text-sm font-medium text-[var(--sahha-text-900)]">
            <Highlight text={hit.label} term={term} />
          </span>
          <span className="shrink-0 rounded-full bg-[var(--sahha-primary-50)] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-[var(--sahha-primary-700)]">
            {t("bar.specialtyLabel")}
          </span>
        </span>
        {hit.sub && <span className="mt-0.5 block truncate text-xs text-[var(--sahha-text-500)]">{hit.sub}</span>}
      </span>
      {active && <KbdHint />}
    </button>
  );
}

function SymptomRow({ hit, term, active, onMouseEnter, onClick }: { hit: SymptomHit; term: string; active: boolean; onMouseEnter: () => void; onClick: () => void; }) {
  const t = useTranslations("search");
  const categoryLabel = t(`bar.categories.${hit.category}` as never) as string;
  return (
    <button
      type="button" role="option" aria-selected={active}
      onMouseDown={(e) => e.preventDefault()}
      onMouseEnter={onMouseEnter}
      onClick={onClick}
      className={cn("flex w-full items-center gap-3 px-3.5 py-2.5 text-start", active ? "bg-[var(--sahha-accent-50)]" : "hover:bg-[var(--sahha-surface-1)]")}
    >
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-amber-50 text-amber-700">
        <IconSparkle className="h-4 w-4" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2">
          <span className="truncate text-sm font-medium text-[var(--sahha-text-900)]">
            <Highlight text={hit.label} term={term} />
          </span>
          <CategoryBadge category={hit.category} label={categoryLabel} />
        </span>
        <span className="mt-0.5 flex items-center gap-1 truncate text-xs text-[var(--sahha-text-500)]">
          <span className="text-[var(--sahha-primary-600)]">→</span>
          <span className="font-medium text-[var(--sahha-primary-700)]">{hit.primaryLabel}</span>
          {hit.alternates.length > 0 && (
            <span className="truncate text-[var(--sahha-text-400)]">&nbsp;· {t("bar.alternates", { count: hit.alternates.length })}</span>
          )}
        </span>
      </span>
      {active && <KbdHint />}
    </button>
  );
}

function CityRow({ hit, term, active, onMouseEnter, onClick }: { hit: CityHit; term: string; active: boolean; onMouseEnter: () => void; onClick: () => void; }) {
  return (
    <button
      type="button" role="option" aria-selected={active}
      onMouseDown={(e) => e.preventDefault()}
      onMouseEnter={onMouseEnter}
      onClick={onClick}
      className={cn("flex w-full items-center gap-3 px-3.5 py-2.5 text-start", active ? "bg-[var(--sahha-accent-50)]" : "hover:bg-[var(--sahha-surface-1)]")}
    >
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[var(--sahha-accent-50)] text-[var(--sahha-accent-700)]">
        <IconPin className="h-4 w-4" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium text-[var(--sahha-text-900)]">
          <Highlight text={hit.label} term={term} />
        </span>
        <span className="block truncate text-xs text-[var(--sahha-text-500)]">{hit.sub}</span>
      </span>
      {active && <KbdHint />}
    </button>
  );
}

function KbdHint() {
  return (
    <span className="hidden items-center gap-1 text-[10px] font-medium text-[var(--sahha-text-400)] sm:inline-flex">
      <kbd className="rounded border border-[var(--sahha-border-default)] bg-white px-1.5 py-0.5 font-sans">↵</kbd>
    </span>
  );
}

type SearchT = ReturnType<typeof useTranslations<"search">>;
function EmptyState({ term, isCity = false, t }: { term: string; isCity?: boolean; t: SearchT }) {
  return (
    <div className="px-5 py-6 text-center">
      <p className="text-sm font-medium text-[var(--sahha-text-700)]">{t("bar.noResults", { term })}</p>
      <p className="mt-1 text-xs text-[var(--sahha-text-500)]">
        {isCity ? t("bar.noResultsCityHint") : t("bar.noResultsQHint")}
      </p>
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between border-b border-[var(--sahha-border-subtle)] px-4 py-1.5 text-[10px] uppercase tracking-widest text-[var(--sahha-text-500)]">
      {children}
    </div>
  );
}

function Footer({ hits }: { hits: number }) {
  const t = useTranslations("search");
  if (hits < 2) return null;
  return (
    <div className="hidden items-center justify-between border-t border-[var(--sahha-border-subtle)] px-4 py-2 text-[11px] text-[var(--sahha-text-500)] sm:flex">
      <span>
        <kbd className="rounded border border-[var(--sahha-border-default)] bg-white px-1.5 py-0.5 font-sans">↑</kbd>
        <kbd className="ms-1 rounded border border-[var(--sahha-border-default)] bg-white px-1.5 py-0.5 font-sans">↓</kbd>
        <span className="ms-2">{t("bar.kbdNavigate")}</span>
      </span>
      <span>
        <kbd className="rounded border border-[var(--sahha-border-default)] bg-white px-1.5 py-0.5 font-sans">Tab</kbd>
        <span className="ms-2">{t("bar.kbdComplete")}</span>
      </span>
      <span>
        <kbd className="rounded border border-[var(--sahha-border-default)] bg-white px-1.5 py-0.5 font-sans">↵</kbd>
        <span className="ms-2">{t("bar.kbdValidate")}</span>
      </span>
    </div>
  );
}

function PhysicianClinicSections({
  physicianHits,
  clinicHits,
  medicamentHits = [],
  tPhysiciansSection,
  tClinicsSection,
  tMedicamentsSection,
  tMedicamentsSeeAll,
  onPickPhysician,
  onPickClinic,
  onPickMedicament,
  onSeeAllMedicaments,
}: {
  physicianHits: PhysicianSuggestion[];
  clinicHits: ClinicSuggestion[];
  medicamentHits?: MedicamentSuggestion[];
  tPhysiciansSection: string;
  tClinicsSection: string;
  tMedicamentsSection?: string;
  tMedicamentsSeeAll?: string;
  onPickPhysician: (hit: PhysicianSuggestion) => void;
  onPickClinic: (slug: string) => void;
  onPickMedicament?: (slug: string) => void;
  onSeeAllMedicaments?: () => void;
}) {
  if (
    physicianHits.length === 0 &&
    clinicHits.length === 0 &&
    medicamentHits.length === 0
  )
    return null;
  return (
    <>
      {physicianHits.length > 0 && (
        <>
          <SectionLabel>
            <span className="inline-flex items-center gap-1.5">
              <span aria-hidden>👨‍⚕️</span> {tPhysiciansSection}
            </span>
          </SectionLabel>
          {physicianHits.map((h) => (
            <PhysicianHitRow
              key={`ph-${h.slug}`}
              hit={h}
              active={false}
              onMouseEnter={() => {/* no-op */}}
              onClick={() => onPickPhysician(h)}
            />
          ))}
        </>
      )}
      {clinicHits.length > 0 && (
        <>
          <SectionLabel>
            <span className="inline-flex items-center gap-1.5">
              <span aria-hidden>🏥</span> {tClinicsSection}
            </span>
          </SectionLabel>
          {clinicHits.map((h) => (
            <ClinicHitRow
              key={`cl-${h.slug}`}
              hit={h}
              active={false}
              onMouseEnter={() => {/* no-op */}}
              onClick={() => onPickClinic(h.slug)}
            />
          ))}
        </>
      )}
      {medicamentHits.length > 0 && tMedicamentsSection && onPickMedicament && (
        <>
          <SectionLabel>
            <span className="inline-flex items-center gap-1.5">
              <span aria-hidden>💊</span> {tMedicamentsSection}
            </span>
          </SectionLabel>
          {medicamentHits.map((h) => (
            <MedicamentHitRow
              key={`me-${h.slug}`}
              hit={h}
              active={false}
              onMouseEnter={() => {/* no-op */}}
              onClick={() => onPickMedicament(h.slug)}
            />
          ))}
          {/* CTA "Voir tous les résultats" → page hub /medicaments?q=...
              Permet à l'utilisateur de tomber sur la page recherche médicament
              complète (filtres labo, classe, remboursable) plutôt que la
              fiche d'un médicament unique. */}
          {tMedicamentsSeeAll && onSeeAllMedicaments && (
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={onSeeAllMedicaments}
              className="flex w-full items-center gap-3 px-4 py-2.5 text-start text-[13px] font-semibold text-[var(--sahha-accent-700)] transition-colors hover:bg-[var(--sahha-accent-50)]"
            >
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[var(--sahha-accent-100)] text-[12px] text-[var(--sahha-accent-700)]">
                →
              </span>
              <span className="flex-1">{tMedicamentsSeeAll}</span>
            </button>
          )}
        </>
      )}
    </>
  );
}

function MedicamentHitRow({
  hit,
  active,
  onMouseEnter,
  onClick,
}: {
  hit: MedicamentSuggestion;
  active: boolean;
  onMouseEnter: () => void;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="option"
      aria-selected={active}
      onMouseDown={(e) => e.preventDefault()}
      onMouseEnter={onMouseEnter}
      onClick={onClick}
      className={cn(
        "flex w-full items-center gap-3 px-4 py-2.5 text-start transition-colors",
        active ? "bg-[var(--sahha-accent-50)]" : "hover:bg-[var(--sahha-surface-1)]",
      )}
    >
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[var(--sahha-accent-50)] text-[18px]">
        <span aria-hidden>💊</span>
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[14px] font-semibold text-[var(--sahha-text-900)]">
          {hit.brand}
        </span>
        {hit.hint && (
          <span className="block truncate text-[12px] text-[var(--sahha-text-500)]">
            {hit.hint}
          </span>
        )}
      </span>
      <IconArrow className="h-4 w-4 shrink-0 text-[var(--sahha-text-400)]" />
    </button>
  );
}

function PhysicianHitRow({
  hit,
  active,
  onMouseEnter,
  onClick,
}: {
  hit: PhysicianSuggestion;
  active: boolean;
  onMouseEnter: () => void;
  onClick: () => void;
}) {
  const isUnclaimed = hit.tier === "unclaimed";
  return (
    <button
      type="button"
      role="option"
      aria-selected={active}
      onMouseDown={(e) => e.preventDefault()}
      onMouseEnter={onMouseEnter}
      onClick={onClick}
      className={cn(
        "flex w-full items-center gap-3 px-4 py-2.5 text-start transition-colors",
        active ? "bg-[var(--sahha-primary-50)]" : "hover:bg-[var(--sahha-surface-1)]",
      )}
    >
      <span className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[var(--sahha-primary-50)] text-[11px] font-bold text-[var(--sahha-primary-700)]">
        {hit.photoUrl ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img src={hit.photoUrl} alt="" className="h-full w-full object-cover" loading="lazy" />
        ) : hit.isCenter ? (
          <span aria-hidden>🏥</span>
        ) : (
          <span aria-hidden>{(hit.name || "?").replace(/^(Dr\.?|Pr\.?)\s+/i, "").split(/\s+/).map((p) => p[0] ?? "").join("").slice(0, 2).toUpperCase() || "?"}</span>
        )}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[14px] font-semibold text-[var(--sahha-text-900)]">
          {hit.name}
          {isUnclaimed ? null : (
            <IconCheckBadge className="ms-1 inline h-3.5 w-3.5 text-[var(--sahha-primary-600)]" />
          )}
        </span>
        <span className="block truncate text-[12px] text-[var(--sahha-text-500)]">
          {[hit.specialty, hit.cityName].filter(Boolean).join(" · ")}
        </span>
      </span>
      <IconArrow className="h-4 w-4 shrink-0 text-[var(--sahha-text-400)]" />
    </button>
  );
}

function ClinicHitRow({
  hit,
  active,
  onMouseEnter,
  onClick,
}: {
  hit: ClinicSuggestion;
  active: boolean;
  onMouseEnter: () => void;
  onClick: () => void;
}) {
  const tClinic = useTranslations("search.clinicTypes");
  const KNOWN_TYPES = ["PRIVATE_CLINIC", "POLYCLINIC", "DENTAL_CLINIC", "AESTHETIC_CLINIC", "IVF_CENTER"] as const;
  const isKnownType = (KNOWN_TYPES as readonly string[]).includes(hit.type);
  const typeText = isKnownType ? tClinic(hit.type as typeof KNOWN_TYPES[number]) : hit.type;
  return (
    <button
      type="button"
      role="option"
      aria-selected={active}
      onMouseDown={(e) => e.preventDefault()}
      onMouseEnter={onMouseEnter}
      onClick={onClick}
      className={cn(
        "flex w-full items-center gap-3 px-4 py-2.5 text-start transition-colors",
        active ? "bg-[var(--sahha-accent-50)]" : "hover:bg-[var(--sahha-surface-1)]",
      )}
    >
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[var(--sahha-accent-50)] text-[18px]">
        {hit.photoEmoji || "🏥"}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[14px] font-semibold text-[var(--sahha-text-900)]">
          {hit.name}
        </span>
        <span className="block truncate text-[12px] text-[var(--sahha-text-500)]">
          {[typeText, hit.cityName].filter(Boolean).join(" · ")}
        </span>
      </span>
      <IconArrow className="h-4 w-4 shrink-0 text-[var(--sahha-text-400)]" />
    </button>
  );
}

function IconCheckBadge({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 16 16" className={className} fill="currentColor" aria-hidden>
      <path d="M8 1.5l1.6 1.2 1.9-.4.9 1.7 1.7.9-.4 1.9 1.2 1.6-1.2 1.6.4 1.9-1.7.9-.9 1.7-1.9-.4L8 14.5 6.4 13.3l-1.9.4-.9-1.7L1.9 11l.4-1.9L1.1 7.5l1.2-1.6-.4-1.9 1.7-.9.9-1.7 1.9.4L8 1.5z"/>
      <path d="M5.5 8l1.7 1.7L10.5 6" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}
